/**
 * `/reach/leads` — slice 70. The fetch, the frame, and what the buttons do;
 * what is drawn, and why, is `ReachLeadsContent.tsx`'s.
 *
 * ## THE READ
 *
 * One `GET /reach/leads` per page and ad filter. The filter and the page live
 * in this component's state, not the URL: a link to "page 3 of one ad's
 * leads" is not something anybody shares, and keeping them out of the query
 * string keeps `/reach/leads` the sidebar's one address for the screen.
 * Picking an ad goes back to page 1, because page 3 of every ad is not page 3
 * of one.
 *
 * ## THE IMPORT
 *
 * Meta's lead export is a CSV; it goes up through `POST /reach/leads/import`,
 * which runs the ordinary import into the system "Reach leads" dataset. The
 * file input is hidden and opened from the button, so the control a reader
 * sees is a real button with a name. After an import the list re-reads and a
 * toast says what happened, in counts — never an address.
 *
 * A failed first read is an `ErrorState` with a retry, as on every sibling
 * page; a failed re-read keeps the list that loaded and says so.
 */
import { PageHeader, Spinner, Card, CardContent, useToast } from '@wend/ui';
import { useRef, useState } from 'react';

import { AppLayout } from '../../../components/AppLayout';
import { ErrorState } from '../../../components/ErrorState';
import { errorMessage } from '../../../lib/api/errors';
import { fetchReachLeads, importReachLeads } from '../../../lib/api/reach-leads';
import { formatCount } from '../../../lib/format';
import { PILLAR_IDENTITY } from '../../../lib/pillars';
import { useAsyncData } from '../../../lib/use-async-data';
import { ReachLeadsContent } from './ReachLeadsContent';

const PAGE_SIZE = 25;

export function ReachLeadsPage() {
  const [page, setPage] = useState(1);
  const [adFilter, setAdFilter] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const leads = useAsyncData(
    (signal) => fetchReachLeads({ page, pageSize: PAGE_SIZE, ...(adFilter === null ? {} : { adId: adFilter }) }, signal),
    [page, adFilter],
  );

  async function upload(file: File): Promise<void> {
    setImporting(true);
    try {
      const result = await importReachLeads(file);
      toast({
        tone: 'success',
        title: `${formatCount(result.imported)} leads imported`,
        description: [
          result.rejected === 0
            ? `${formatCount(result.newPeople)} of them are new people.`
            : `${formatCount(result.newPeople)} new people; ${formatCount(result.rejected)} rows could not be read.`,
          // An export is history: older leads land, but no new-lead flow greets them.
          result.historical === 0
            ? null
            : `${formatCount(result.historical)} arrived more than 3 days ago or carry no time Wend can read, so no new-lead flow starts for them.`,
        ]
          .filter((part): part is string => part !== null)
          .join(' '),
      });
      setPage(1);
      leads.reload();
    } catch (cause) {
      toast({ tone: 'error', title: 'The import did not go through', description: errorMessage(cause) });
    } finally {
      setImporting(false);
      if (fileInput.current !== null) fileInput.current.value = '';
    }
  }

  return (
    <AppLayout title="Leads">
      <PageHeader
        eyebrow={PILLAR_IDENTITY.reach.label}
        icon={PILLAR_IDENTITY.reach.icon}
        lede="The people your ads brought in, by the ad that brought them."
      />
      <input
        ref={fileInput}
        type="file"
        accept=".csv,text/csv"
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file !== undefined) void upload(file);
        }}
      />
      {leads.data === null ? (
        <Card>
          {leads.loadingFirst ? (
            <CardContent className="flex items-center gap-3 p-[18px] text-[0.92rem] text-muted">
              <Spinner size="md" label={null} />
              <span role="status">Loading…</span>
            </CardContent>
          ) : (
            <ErrorState error={leads.error} title="Could not load your leads" onRetry={leads.reload} />
          )}
        </Card>
      ) : (
        <>
          {leads.status === 'error' && (
            <p role="alert" className="text-[0.85rem] text-warn">
              Could not re-read the leads; this is the list that last loaded. {errorMessage(leads.error)}
            </p>
          )}
          <ReachLeadsContent
            leads={leads.data}
            adFilter={adFilter}
            handlers={{
              onPickAd: (adId) => {
                setAdFilter(adId);
                setPage(1);
              },
              onPage: setPage,
              onImport: () => fileInput.current?.click(),
              importing,
            }}
          />
        </>
      )}
    </AppLayout>
  );
}
